import {
  registerTransformation,
  TransformationType,
} from "../../calculation/Calculation";
import { TransformationTags } from "../../calculation/TransformationTags";
import { DungeonFloor } from "../../exploration/dungeons/DungeonFloor";
import { isDungeonFloorEverCompleted } from "../../exploration/dungeons/Dungeons";
import { getSuccessfulExplorationsForFloor } from "../../exploration/Exploration";
import {
  hasFlag,
  hasGlobalFlag,
  hasGlobalFlagIncludingNextRun,
  setFlag,
  setGlobalFlagForNextRun,
} from "../../Flags";
import { PlayerContextState } from "../../PlayerContext";
import { addContextListener } from "../../PlayerContextListeners";
import { getResourceAmount, grantResource, Resource } from "../../Resources";
import {
  getMaxOfMaxPrimarySchoolLevels,
  getMaxPrimarySchoolLevel,
  getSchoolLevel,
  getUnlockedSchools,
  grantSchoolExp,
} from "../../spells/ElementsAndSchools";
import { Storyline, Storylines } from "../../storylines/Storylines";
import { registerRandomEventTrigger } from "../autorandom/RandomEventTrigger";
import { buildEvent } from "../EventMessageBuilder";
import { triggerEvent } from "../Events";
import { EventTag } from "../GameEvent";

const memoryChamberEventBuilder = buildEvent(
  "memory_chamber",
  "(Storyline) The Memory Chamber",
  [EventTag.STORYLINE],
);
memoryChamberEventBuilder
  .setOnTrigger(setFlag("memory_chamber_triggered"))
  .createMessage()
  .setSimpleDescription(
    `Your researcher approaches you. In her hands is a large blueprint.
    
"I have figured out an interesting idea," she says. "There is a way we could build a magical storage, not for our resources, but for our memories."`,
  )
  .addOption(
    "Continue",
    memoryChamberEventBuilder
      .createMessage()
      .setSimpleDescription(
        `Confused, you ask her to explain further.
      
"Well, you see," she continues. "We are doing a ton of research right now, and it would be a shame if it were to be lost to future generations. Not only that, but the exploration we have been doing is also something they would be able to benefit from... as long as they know about it, of course."

You ponder her proposal and look at the blueprint. The costs are definitely hefty.`,
      )
      .addOption(
        "Build the Memory Chamber (5000 Mana, 30K Stone)",
        {
          transform: (state) => {
            state = grantResource(Resource.Mana, -5000)(state);
            state = grantResource(Resource.Stone, -30000)(state);
            return state;
          },
          isEnabled: (state) =>
            getResourceAmount(state, Resource.Mana) >= 5000 &&
            getResourceAmount(state, Resource.Stone) >= 30000,
        },
        memoryChamberEventBuilder
          .createMessage()
          .setSimpleDescription(
            `You decide to build the Memory Chamber and motion to your researcher to do so.
    
A few days later, your researcher takes you to the freshly built chamber: a small room in a corner of the campus. You enter it and enchant it with plenty of Mana. The room is mostly empty bookshelves right now.`,
          )
          .addOption(
            "Continue",
            {
              transform: (state) => {
                state = setGlobalFlagForNextRun("memory_chamber_completed")(
                  state,
                );
                state = setFlag("memory_chamber_exp_granted")(state);
                return state;
              },
            },
            memoryChamberEventBuilder.createMessage()
              .setSimpleDescription(`"We'll make sure to fill it with the fruits of our research", says the researcher. "We will leave behind a lasting legacy." You nod, satisfied, knowing that your efforts will not be forgotten in the future.

**You have completed the "The Memory Chamber" storyline! You will begin future runs with some experience on your schools based on each school's MPL (Maximum Primary Level). In addition, areas you have beaten the boss of in the past will require less exploration depending on your highest MPL!**`),
          ),
      ),
  );

registerTransformation(
  [[TransformationTags.ExplorationBossRequirement]],
  "MemoryChamberExploration",
  TransformationType.Multiplier,
  (state, params) => {
    const floor = params.floor as DungeonFloor;
    return isDungeonFloorEverCompleted(state, floor)
      ? calculateMemoryChamberExplorationMultiplier(state)
      : 1.0;
  },
);

addContextListener("memory_chamber_post_retirement", (newState) => {
  if (
    hasGlobalFlag(newState, "memory_chamber_completed") &&
    !hasFlag(newState, "memory_chamber_exp_granted")
  ) {
    return (state) => {
      const schools = getUnlockedSchools(state);
      schools.forEach((school) => {
        const mpl = getMaxPrimarySchoolLevel(state, school);
        const expToGrant = 100 * Math.pow(1.25, mpl);
        state = grantSchoolExp(school, expToGrant)(state);
      });
      state = setFlag("memory_chamber_exp_granted")(state);
      return state;
    };
  }
});

export function calculateMemoryChamberExplorationMultiplier(
  state: PlayerContextState,
): number {
  return hasGlobalFlag(state, "memory_chamber_completed")
    ? 0.7 -
        Math.min(
          Math.max(getMaxOfMaxPrimarySchoolLevels(state) - 15, 0) * 0.016,
          0.4,
        )
    : 1.0;
}

const memoryChamberEvent = memoryChamberEventBuilder.build();

registerRandomEventTrigger(
  memoryChamberEvent,
  60,
  true,
  (state) =>
    !hasFlag(state, "memory_chamber_triggered") &&
    state.primarySchool != undefined &&
    getSchoolLevel(state, state.primarySchool) >= 15 &&
    hasFlag(state, "exploration_unlocked") &&
    !hasGlobalFlag(state, "memory_chamber_completed"),
);

addContextListener("safeguard_memory_chamber_trigger", (state) => {
  if (
    !hasFlag(state, "memory_chamber_triggered") &&
    !hasGlobalFlag(state, "memory_chamber_completed") &&
    state.primarySchool != undefined &&
    getSuccessfulExplorationsForFloor(state, "livingForestGrove") > 10
  ) {
    // Safeguard unlock the alchemy lab.
    return triggerEvent(memoryChamberEvent, {});
  }
});

Storylines.register(
  new Storyline(
    "The Memory Chamber",
    3060,
    (state) => hasGlobalFlagIncludingNextRun(state, "memory_chamber_completed"),
    (state) => hasGlobalFlag(state, "memory_chamber_completed"),
    "There's a chance of this happening randomly if your primary School is Lv15 or higher...",
    "Start new runs with exp on every school depending on its MPL; Exploration amount requirement to unlock boss for previously beaten areas halved",
    (state) => true,
  ),
);

export default memoryChamberEvent;
